/**
 * Submission Harvest + Outcome Capture store (Specs/win-loss-harvest-spec.md)
 *
 * Owns the two status-transition dialogs and the overview affordances:
 *  - "Mark as Submitted": harvest selection BEFORE the transition commits
 *    ("Skip" submits without harvesting — the status change is never blocked).
 *    It also opens when the bid has files to keep as lodged (R3 T6b); the
 *    dialog freezes those before calling confirmSubmit / skipSubmit.
 *  - Outcome dialog (R4: any of the five outcomes). Opened from the stage
 *    control, the Submit page or the overview; saving through `_outcome`
 *    closes the bid in one step. The board's won/lost drop still commits the
 *    status first, then asks, as before.
 */

import { create } from 'zustand'
import {
  getHarvestStatus,
  submitBidWithHarvest,
  lateHarvest,
  recordOutcome,
  type HarvestStatusResponse,
  type RecordOutcomeInput,
} from '@/lib/api'
import { BidStatus } from '@/lib/schemas'
import type { BidOutcomeValue } from '@/lib/bid-lifecycle'
import { useBidsStore } from '@/stores/bids-store'
import { useSubmissionFilesStore } from '@/stores/submission-files-store'
import { captureEvent } from '@/lib/posthog'

/** Server refusal for a restricted-bid harvest sent without confirmation. */
const RESTRICTED_HARVEST_NEEDS_CONFIRMATION = 'RESTRICTED_BID_HARVEST_NEEDS_CONFIRMATION'

function isRestrictedHarvestRefusal(error: unknown): boolean {
  return (
    error instanceof Error &&
    'code' in error &&
    (error as { code?: unknown }).code === RESTRICTED_HARVEST_NEEDS_CONFIRMATION
  )
}

interface SubmitDialogState {
  bidId: string
  bidTitle: string
}

interface OutcomeDialogState {
  bidId: string
  bidTitle: string
  /** Preselected outcome; null lets the user pick. */
  outcome: BidOutcomeValue | null
}

/**
 * A harvest of a restricted bid waiting on the user's say-so. The library is
 * org-wide, so saving widens who can see the bid. Nothing is sent until they
 * confirm; cancelling drops it.
 */
interface RestrictedWarningState {
  kind: 'submit' | 'late'
  bidId: string
  artifactIds: string[]
}

interface HarvestOptions {
  /** The user confirmed saving a restricted bid to the org-wide library. */
  confirmRestrictedLibrary?: boolean
}

interface HarvestState {
  /** Harvest/outcome state for the bid currently in view (overview + dialogs). */
  statusBidId: string | null
  status: HarvestStatusResponse | null
  isLoadingStatus: boolean

  submitDialog: SubmitDialogState | null
  outcomeDialog: OutcomeDialogState | null
  isSaving: boolean
  restrictedWarning: RestrictedWarningState | null

  loadStatus: (bidId: string, opts?: { force?: boolean }) => Promise<void>
  /**
   * Entry point for a → submitted transition. Opens the harvest dialog when
   * the org has harvest on and the bid has artifacts; otherwise commits the
   * plain status change immediately.
   */
  requestSubmit: (bid: { id: string; title: string }) => Promise<void>
  /** A restricted bid opens the library warning first unless already confirmed. */
  confirmSubmit: (artifactIds: string[], options?: HarvestOptions) => Promise<void>
  /** "Skip" — submit without harvesting. */
  skipSubmit: () => Promise<void>
  closeSubmitDialog: () => void

  /** The board's → won/lost drop: commit the status first, then ask. */
  requestOutcome: (bid: { id: string; title: string }, outcome: 'won' | 'lost') => Promise<void>
  /**
   * Record result / Edit result (no status change until saved). Loads the
   * bid's saved outcome so an edit opens pre-filled.
   */
  openOutcomeDialog: (bid: { id: string; title: string }, outcome?: BidOutcomeValue | null) => void
  saveOutcome: (input: RecordOutcomeInput) => Promise<void>
  closeOutcomeDialog: () => void

  /** Late harvest from the overview affordance (same restricted-bid warning). */
  harvestNow: (bidId: string, artifactIds: string[], options?: HarvestOptions) => Promise<void>

  /** "Save to library" on the restricted-bid warning: send, with confirmation. */
  confirmRestrictedHarvest: () => Promise<void>
  /** Cancel on the restricted-bid warning: nothing is sent or saved. */
  cancelRestrictedHarvest: () => void
}

/** Whether the loaded harvest status says this bid is restricted. */
function isRestrictedBid(state: Pick<HarvestState, 'status' | 'statusBidId'>, bidId: string): boolean {
  return state.statusBidId === bidId && state.status?.bidVisibility === 'restricted'
}

export const useHarvestStore = create<HarvestState>((set, get) => ({
  statusBidId: null,
  status: null,
  isLoadingStatus: false,
  submitDialog: null,
  outcomeDialog: null,
  isSaving: false,
  restrictedWarning: null,

  loadStatus: async (bidId, opts) => {
    const state = get()
    if (!opts?.force && state.statusBidId === bidId && (state.status || state.isLoadingStatus)) return
    set({ statusBidId: bidId, isLoadingStatus: true, ...(state.statusBidId !== bidId ? { status: null } : {}) })
    try {
      const status = await getHarvestStatus(bidId)
      if (get().statusBidId === bidId) set({ status, isLoadingStatus: false })
    } catch {
      if (get().statusBidId === bidId) set({ status: null, isLoadingStatus: false })
    }
  },

  requestSubmit: async (bid) => {
    set({ statusBidId: bid.id, isLoadingStatus: true })
    let status: HarvestStatusResponse | null = null
    // The files the dialog would offer to keep as lodged (R3 T6b) load beside
    // the harvest status. A failed load just means nothing to keep.
    const filesLoad = useSubmissionFilesStore.getState().load(bid.id).catch(() => undefined)
    try {
      status = await getHarvestStatus(bid.id)
      set({ status, isLoadingStatus: false })
    } catch {
      set({ isLoadingStatus: false })
    }
    await filesLoad
    const files = useSubmissionFilesStore.getState()
    const hasFilesToKeep = files.bidId === bid.id && (files.data?.items.some((item) => item.file !== null) ?? false)

    if ((status && status.autoHarvestEnabled && status.artifacts.length > 0) || hasFilesToKeep) {
      set({ submitDialog: { bidId: bid.id, bidTitle: bid.title } })
      return
    }

    // Org switch off / nothing to harvest → plain transition, no ceremony
    await useBidsStore.getState().updateBidStatus(bid.id, 'submitted')
    void useBidsStore.getState().refreshBid(bid.id)
  },

  confirmSubmit: async (artifactIds, options) => {
    const dialog = get().submitDialog
    if (!dialog) return
    const confirmed = options?.confirmRestrictedLibrary === true
    if (artifactIds.length > 0 && !confirmed && isRestrictedBid(get(), dialog.bidId)) {
      set({ restrictedWarning: { kind: 'submit', bidId: dialog.bidId, artifactIds } })
      return
    }
    const previousStatus = BidStatus.safeParse(get().status?.bidStatus)
    set({ isSaving: true })
    try {
      useBidsStore.getState().moveBid(dialog.bidId, 'submitted')
      if (confirmed) {
        await submitBidWithHarvest(dialog.bidId, artifactIds, { confirmRestrictedLibrary: true })
      } else {
        await submitBidWithHarvest(dialog.bidId, artifactIds)
      }
      captureEvent('harvest_confirmed', { bidId: dialog.bidId, artifactCount: artifactIds.length })
      set({ submitDialog: null })
      void get().loadStatus(dialog.bidId, { force: true })
      void useBidsStore.getState().refreshBid(dialog.bidId)
    } catch (error) {
      if (!isRestrictedHarvestRefusal(error)) throw error
      // The bid became restricted after the dialog loaded: the server changed
      // nothing, so undo the optimistic move and ask first.
      if (previousStatus.success) useBidsStore.getState().moveBid(dialog.bidId, previousStatus.data)
      set({ restrictedWarning: { kind: 'submit', bidId: dialog.bidId, artifactIds } })
      void get().loadStatus(dialog.bidId, { force: true })
    } finally {
      set({ isSaving: false })
    }
  },

  skipSubmit: async () => {
    const dialog = get().submitDialog
    if (!dialog) return
    set({ submitDialog: null })
    captureEvent('harvest_skipped', { bidId: dialog.bidId })
    await useBidsStore.getState().updateBidStatus(dialog.bidId, 'submitted')
    void useBidsStore.getState().refreshBid(dialog.bidId)
  },

  closeSubmitDialog: () => set({ submitDialog: null }),

  requestOutcome: async (bid, outcome) => {
    // The status change is never held hostage — commit first, then ask
    await useBidsStore.getState().updateBidStatus(bid.id, outcome)
    set({ outcomeDialog: { bidId: bid.id, bidTitle: bid.title, outcome } })
    void get().loadStatus(bid.id, { force: true })
  },

  openOutcomeDialog: (bid, outcome = null) => {
    set({ outcomeDialog: { bidId: bid.id, bidTitle: bid.title, outcome } })
    void get().loadStatus(bid.id, { force: true })
  },

  saveOutcome: async (input) => {
    const dialog = get().outcomeDialog
    if (!dialog) return
    set({ isSaving: true })
    try {
      // One call: the outcome row, and the bid closed with it (R4 T2).
      await recordOutcome(dialog.bidId, input)
      captureEvent('outcome_saved', {
        bidId: dialog.bidId,
        outcome: input.outcome,
        hasReason: input.primaryReason != null,
        hasFeedback: Boolean(input.feedbackText),
        hasDebrief: input.debriefDocumentId != null,
      })
      set({ outcomeDialog: null })
      void get().loadStatus(dialog.bidId, { force: true })
      await useBidsStore.getState().refreshBid(dialog.bidId)
    } finally {
      set({ isSaving: false })
    }
  },

  closeOutcomeDialog: () => {
    const dialog = get().outcomeDialog
    if (dialog) captureEvent('outcome_dialog_dismissed', { bidId: dialog.bidId, outcome: dialog.outcome })
    set({ outcomeDialog: null })
  },

  harvestNow: async (bidId, artifactIds, options) => {
    if (artifactIds.length === 0) return
    const confirmed = options?.confirmRestrictedLibrary === true
    if (!confirmed && isRestrictedBid(get(), bidId)) {
      set({ restrictedWarning: { kind: 'late', bidId, artifactIds } })
      return
    }
    set({ isSaving: true })
    try {
      if (confirmed) {
        await lateHarvest(bidId, artifactIds, { confirmRestrictedLibrary: true })
      } else {
        await lateHarvest(bidId, artifactIds)
      }
      captureEvent('harvest_confirmed', { bidId, artifactCount: artifactIds.length, late: true })
      void get().loadStatus(bidId, { force: true })
    } catch (error) {
      if (!isRestrictedHarvestRefusal(error)) throw error
      set({ restrictedWarning: { kind: 'late', bidId, artifactIds } })
      void get().loadStatus(bidId, { force: true })
    } finally {
      set({ isSaving: false })
    }
  },

  confirmRestrictedHarvest: async () => {
    const warning = get().restrictedWarning
    if (!warning) return
    set({ restrictedWarning: null })
    if (warning.kind === 'submit') {
      await get().confirmSubmit(warning.artifactIds, { confirmRestrictedLibrary: true })
    } else {
      await get().harvestNow(warning.bidId, warning.artifactIds, { confirmRestrictedLibrary: true })
    }
  },

  cancelRestrictedHarvest: () => set({ restrictedWarning: null }),
}))
